import type { CatalogSectionProps } from '@/features/publicsite/types/catalog/CatalogSectionProps';
import { publicCatalog } from '@/i18n/dictionaries/publicCatalog';
import { useTranslation } from '@/i18n/useTranslation';
import { usePublicPageTitle } from '@/features/publicsite/usePublicPageTitle';

/**
 * The frame every public catalogue page uses: eyebrow, heading, lead paragraph, then either a grid
 * of cards or the shared empty state.
 *
 * `items` is passed already loaded rather than fetched here, because the five pages differ in what
 * they load (one of them loads two lists) and a hook that tried to cover all of them would take
 * more configuration than it saved.
 */
export function CatalogSection<T>({
  eyebrow,
  heading,
  lead,
  items,
  renderCard,
  emptyMessage,
  headLink,
  children,
  after,
}: CatalogSectionProps<T>) {
  const { t } = useTranslation(publicCatalog);
  usePublicPageTitle(heading);

  return (
    <section className="container section">
      {headLink ? (
        <div className="section-head">
          <div>
            {eyebrow && <p className="eyebrow">{eyebrow}</p>}
            <h1>{heading}</h1>
            {lead && <p className="muted">{lead}</p>}
          </div>
          {headLink}
        </div>
      ) : (
        <>
          {eyebrow && <p className="eyebrow">{eyebrow}</p>}
          <h1>{heading}</h1>
          {lead && <p className="muted">{lead}</p>}
        </>
      )}
      {children}
      {items && items.length > 0 && <div className="card-grid">{items.map(renderCard)}</div>}
      {/* Only once the list has actually arrived: `items === null` is "still loading", and
          printing "no records yet" during the fetch tells the visitor something untrue. */}
      {/* A div, not a <p>: an empty state that offers a way out is a title and a hint, and a
          paragraph cannot legally contain either. The shared line still renders as one line. */}
      {items && items.length === 0 && (
        <div className="empty-state">{emptyMessage ?? t('emptyState')}</div>
      )}
      {after}
    </section>
  );
}
